import { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, ReceiptText } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { Modal } from '@/components/ui/modal';
import { Badge } from '@/components/ui/badge';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useAdminTenants } from '@/hooks/use-admin';
import { toast } from '@/stores/toast-store';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import {
  usePaymentReconAlerts,
  useResolvePaymentReconAlert,
  useRunPaymentReconCheck,
  type PaymentReconAlert,
  type PaymentReconStatus,
} from '@/hooks/use-admin-payment-recon';

// Super admin only (AdminRouter). Card charges the processor settled with no
// matching Supernova order — usually a payment the terminal approved that the
// POS never saved. Filled daily by the payment-recon job.

const TABS: { value: PaymentReconStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'auto_resolved', label: 'Matched later' },
  { value: 'all', label: 'All' },
];

const STATUS_BADGE: Record<PaymentReconAlert['status'], { variant: 'warning' | 'success' | 'default'; label: string }> =
  {
    open: { variant: 'warning', label: 'Open' },
    resolved: { variant: 'success', label: 'Resolved' },
    auto_resolved: { variant: 'default', label: 'Matched later' },
  };

const TH = 'px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700';
const TD = 'px-4 py-3 text-sm text-muted-foreground';

export function AdminPaymentReconPage() {
  const [status, setStatus] = useState<PaymentReconStatus>('open');
  const [page, setPage] = useState(1);
  const [resolving, setResolving] = useState<PaymentReconAlert | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Search as the admin types, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: tenantsData } = useAdminTenants({ perPage: 200, isActive: 'true' });
  const tenantOptions = useMemo(
    () => ((tenantsData?.data ?? []) as { id: string; name: string }[]).map((t) => ({ value: t.id, label: t.name })),
    [tenantsData],
  );
  const { data, isLoading } = usePaymentReconAlerts({
    status,
    page,
    perPage: 25,
    tenantId: tenantId || undefined,
    from: from || undefined,
    to: to || undefined,
    q: search || undefined,
  });
  const hasFilters = !!(tenantId || from || to || search);
  const runMutation = useRunPaymentReconCheck();

  const alerts = data?.data ?? [];
  const totals = data?.totals;
  const meta = data?.meta;

  function handleRunNow() {
    runMutation.mutate(undefined, {
      onSuccess: (res) => {
        const r = res.data;
        toast.success(
          `Check complete — ${r.stores} store${r.stores === 1 ? '' : 's'} checked, ${r.newlyOpened} new, ${r.autoResolved} matched later, ${r.outOfScopeDeleted + r.expiredDeleted} cleared, ${r.openTotal} open`,
        );
      },
      onError: () => toast.error('The check could not run — try again'),
    });
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Charges With No Order"
        description="Card charges the processor settled that have no matching Supernova order — usually a payment approved at the terminal that the POS never saved. Checked automatically every day."
      >
        <button
          type="button"
          onClick={handleRunNow}
          disabled={runMutation.isPending}
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
        >
          {runMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Run Check Now
        </button>
      </PageHeader>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-lg border border-border bg-white p-0.5">
            {TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => {
                  setStatus(t.value);
                  setPage(1);
                }}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  status === t.value ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-56">
              <SearchableSelect
                options={tenantOptions}
                value={tenantId}
                onChange={(v) => {
                  setTenantId(v);
                  setPage(1);
                }}
                placeholder="All tenants"
                searchPlaceholder="Search tenants..."
                clearable
              />
            </div>
            <input
              type="date"
              aria-label="Settled from"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm text-foreground"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              aria-label="Settled to"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm text-foreground"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Amount, last 4 or approval code"
              className="w-64 rounded-lg border border-border bg-white px-3 py-1.5 text-sm text-foreground"
            />
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setTenantId('');
                  setFrom('');
                  setTo('');
                  setSearchInput('');
                  setSearch('');
                  setPage(1);
                }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
          {totals && (
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{totals.count}</span> charge
              {totals.count === 1 ? '' : 's'} ·{' '}
              <span className="font-semibold text-foreground">{formatCurrency(totals.amount)}</span>
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Loading charges...</span>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className={TH}>Settled</th>
                  <th className={TH}>Tenant</th>
                  <th className={TH}>Location</th>
                  <th className={TH}>Amount</th>
                  <th className={TH}>Card</th>
                  <th className={TH}>Approval Code</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alerts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <ReceiptText className="h-9 w-9 text-muted-foreground/40" />
                        <p className="text-sm font-medium text-muted-foreground">
                          {hasFilters
                            ? 'No charges match these filters'
                            : status === 'open'
                              ? 'Every settled charge has an order'
                              : 'Nothing here'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  alerts.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className={TD}>{formatDateTime(a.fundingDate)}</td>
                      <td className={TD}>{a.tenantName ?? a.tenantId.slice(0, 8)}</td>
                      <td className={TD}>{a.locationName ?? '—'}</td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{formatCurrency(a.amount)}</td>
                      <td className={TD}>
                        {a.brand ?? 'Card'} {a.lastFour ? `•• ${a.lastFour}` : ''}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-foreground">{a.authCode ?? '—'}</td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS_BADGE[a.status].variant}>{STATUS_BADGE[a.status].label}</Badge>
                        {a.resolutionNote && a.status !== 'open' && (
                          <p className="mt-1 max-w-[220px] text-xs text-muted-foreground">{a.resolutionNote}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.status === 'open' && (
                          <button
                            type="button"
                            onClick={() => setResolving(a)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Resolve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {meta && meta.totalPages > 1 && (
          <DataTablePagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            perPage={meta.perPage}
            onPageChange={setPage}
          />
        )}
      </div>

      {resolving && <ResolveModal alert={resolving} onClose={() => setResolving(null)} />}
    </div>
  );
}

function ResolveModal({ alert, onClose }: { alert: PaymentReconAlert; onClose: () => void }) {
  const [note, setNote] = useState('');
  const resolve = useResolvePaymentReconAlert();

  function handleSave() {
    if (!note.trim()) return toast.error('Add a note saying how it was resolved');
    resolve.mutate(
      { id: alert.id, note: note.trim() },
      {
        onSuccess: () => {
          toast.success('Marked as resolved');
          onClose();
        },
        onError: () => toast.error('Could not save — try again'),
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Resolve Charge"
      subtitle={`${formatCurrency(alert.amount)} · ${alert.brand ?? 'Card'} ${alert.lastFour ? `•• ${alert.lastFour}` : ''} · approval ${alert.authCode ?? '—'}`}
      widthClass="max-w-[560px]"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={resolve.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {resolve.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Mark Resolved
          </button>
        </div>
      }
    >
      <div className="space-y-2">
        <label htmlFor="resolve-note" className="text-sm font-medium text-foreground">
          How was it resolved?
        </label>
        <textarea
          id="resolve-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          placeholder="e.g. Order re-entered at the store (#160); customer refunded; duplicate charge voided"
          className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      </div>
    </Modal>
  );
}
